import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, AlertCircle, Phone, RefreshCw, Search, Eye
} from 'lucide-react';
import adminService from '../../services/admin/adminService';
import {
  SupportTicket,
  Dispute,
  ScheduledCallback,
  SupportStatistics,
  SupportTicketStatus,
  SupportTicketPriority,
  SupportTicketType,
  DisputeStatus,
  DisputeType,
  CallbackStatus
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type ViewMode = 'tickets' | 'callbacks' | 'disputes';

const SupportDisputeManagement: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('tickets');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState<SupportStatistics | null>(null);

  // Tickets
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [ticketStatusFilter, setTicketStatusFilter] = useState<SupportTicketStatus | ''>('');
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState<SupportTicketPriority | ''>('');
  const [ticketTypeFilter, setTicketTypeFilter] = useState<SupportTicketType | ''>('');
  const [ticketSearchQuery, setTicketSearchQuery] = useState('');

  // Callbacks
  const [callbacks, setCallbacks] = useState<ScheduledCallback[]>([]);
  const [callbackPage, setCallbackPage] = useState(1);
  const [callbackTotalPages, setCallbackTotalPages] = useState(1);
  const [callbackStatusFilter, setCallbackStatusFilter] = useState<CallbackStatus | ''>('');

  // Disputes
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [disputePage, setDisputePage] = useState(1);
  const [disputeTotalPages, setDisputeTotalPages] = useState(1);
  const [disputeStatusFilter, setDisputeStatusFilter] = useState<DisputeStatus | ''>('');
  const [disputeTypeFilter, setDisputeTypeFilter] = useState<DisputeType | ''>('');

  useEffect(() => {
    loadData();
  }, [viewMode, currentPage, callbackPage, disputePage, ticketStatusFilter, ticketPriorityFilter, ticketTypeFilter, callbackStatusFilter, disputeStatusFilter, disputeTypeFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (viewMode === 'tickets') {
        await loadTickets();
      } else if (viewMode === 'callbacks') {
        await loadCallbacks();
      } else if (viewMode === 'disputes') {
        await loadDisputes();
      }
      await loadStatistics();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await adminService.getSupportStatistics();
      setStatistics(stats);
    } catch (error: any) {
      console.error('Failed to load statistics:', error);
    }
  };

  const loadTickets = async () => {
    const params: any = {
      page: currentPage,
      limit: 20
    };
    if (ticketStatusFilter) params.status = ticketStatusFilter;
    if (ticketPriorityFilter) params.priority = ticketPriorityFilter;
    if (ticketTypeFilter) params.type = ticketTypeFilter;

    const response = await adminService.getAllSupportTickets(params);
    setTickets(response.tickets);
    setTotalPages(response.pagination.total_pages);
    setTotalTickets(response.pagination.total_tickets);
  };

  const loadCallbacks = async () => {
    const params: any = {
      page: callbackPage,
      limit: 20
    };
    if (callbackStatusFilter) params.status = callbackStatusFilter;

    const response = await adminService.getScheduledCallbacks(params);
    setCallbacks(response.callbacks);
    setCallbackTotalPages(response.pagination.total_pages);
  };

  const loadDisputes = async () => {
    const params: any = {
      page: disputePage,
      limit: 20
    };
    if (disputeStatusFilter) params.status = disputeStatusFilter;
    if (disputeTypeFilter) params.type = disputeTypeFilter;

    const response = await adminService.getAllDisputes(params);
    setDisputes(response.disputes);
    setDisputeTotalPages(response.pagination.total_pages);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleViewTicket = (ticket: SupportTicket) => {
    navigate(`/admin/support/tickets/${ticket.ticket_id}`);
  };

  const handleViewDispute = (dispute: Dispute) => {
    navigate(`/admin/disputes/${dispute.dispute_id}`);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      pending: 'bg-orange-100 text-orange-800',
      under_review: 'bg-purple-100 text-purple-800',
      rejected: 'bg-red-100 text-red-800',
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      missed: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const badges: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return badges[priority] || 'bg-gray-100 text-gray-800';
  };

  const filteredTickets = tickets.filter(ticket => {
    if (!ticketSearchQuery) return true;
    const query = ticketSearchQuery.toLowerCase();
    return (
      ticket.ticket_number.toLowerCase().includes(query) ||
      ticket.subject.toLowerCase().includes(query) ||
      ticket.creator_name.toLowerCase().includes(query) ||
      ticket.creator_email.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Support & Dispute Management</h1>
          <p className="text-secondary-600 mt-1">Manage support tickets, callbacks, and disputes</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={refreshing ? 'animate-spin' : ''} size={18} />
          Refresh
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-secondary-600">Open Tickets</p>
            <p className="text-2xl font-bold text-secondary-900 mt-1">{statistics.open_tickets}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-secondary-600">In Progress</p>
            <p className="text-2xl font-bold text-yellow-600 mt-1">{statistics.in_progress_tickets}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-secondary-600">Resolved</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{statistics.resolved_tickets}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-secondary-600">Today's Tickets</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{statistics.today_tickets}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-secondary-600">Pending Disputes</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{statistics.pending_disputes}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-secondary-600">Under Review</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">{statistics.under_review_disputes}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          {[
            { id: 'tickets' as ViewMode, label: 'Support Tickets', icon: MessageSquare },
            { id: 'callbacks' as ViewMode, label: 'Callbacks', icon: Phone },
            { id: 'disputes' as ViewMode, label: 'Disputes', icon: AlertCircle }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => {
                setViewMode(id);
                setCurrentPage(1);
                setCallbackPage(1);
                setDisputePage(1);
              }}
              className={`flex-1 px-6 py-4 flex items-center justify-center gap-2 transition-colors ${
                viewMode === id
                  ? 'border-b-2 border-primary-500 text-primary-600 font-semibold bg-primary-50'
                  : 'text-secondary-600 hover:bg-gray-50'
              }`}
            >
              <Icon size={20} />
              {label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Support Tickets View */}
          {viewMode === 'tickets' && (
            <>
              {/* Filters */}
              <div className="mb-6 space-y-4">
                <div className="flex gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search tickets..."
                      value={ticketSearchQuery}
                      onChange={(e) => setTicketSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <select
                    value={ticketStatusFilter}
                    onChange={(e) => {
                      setTicketStatusFilter(e.target.value as SupportTicketStatus | '');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <select
                    value={ticketPriorityFilter}
                    onChange={(e) => {
                      setTicketPriorityFilter(e.target.value as SupportTicketPriority | '');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <select
                    value={ticketTypeFilter}
                    onChange={(e) => {
                      setTicketTypeFilter(e.target.value as SupportTicketType | '');
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Types</option>
                    <option value="support">Support</option>
                    <option value="technical">Technical</option>
                    <option value="payment">Payment</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Tickets Table */}
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <RefreshCw className="animate-spin text-primary-500" size={32} />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12 text-secondary-600">
                  <MessageSquare size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>No tickets found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Ticket #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Subject</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Creator</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Priority</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Assigned To</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTickets.map((ticket) => (
                          <tr key={ticket.ticket_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-secondary-900">{ticket.ticket_number}</span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-secondary-900">{ticket.subject}</div>
                              <div className="text-xs text-secondary-500 mt-1 line-clamp-1">{ticket.description}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-secondary-900">{ticket.creator_name}</div>
                              <div className="text-xs text-secondary-500">{ticket.creator_email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-secondary-700 capitalize">{ticket.ticket_type}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(ticket.priority)}`}>
                                {ticket.priority}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(ticket.status)}`}>
                                {ticket.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-secondary-700">
                                {ticket.assigned_admin_name || 'Unassigned'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleViewTicket(ticket)}
                                className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                              >
                                <Eye size={18} />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-secondary-600">
                        Page {currentPage} of {totalPages} ({totalTickets} total)
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Callbacks View */}
          {viewMode === 'callbacks' && (
            <>
              <div className="mb-6">
                <select
                  value={callbackStatusFilter}
                  onChange={(e) => {
                    setCallbackStatusFilter(e.target.value as CallbackStatus | '');
                    setCallbackPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="missed">Missed</option>
                </select>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <RefreshCw className="animate-spin text-primary-500" size={32} />
                </div>
              ) : callbacks.length === 0 ? (
                <div className="text-center py-12 text-secondary-600">
                  <Phone size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>No callbacks found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Ticket</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Scheduled At</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Assigned To</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {callbacks.map((callback) => (
                          <tr key={callback.callback_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-secondary-900">{callback.ticket_number}</div>
                              <div className="text-xs text-secondary-500">{callback.ticket_subject}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-secondary-900">
                                {new Date(callback.scheduled_at).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-secondary-700 capitalize">{callback.callback_type.replace('_', ' ')}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-secondary-900">{callback.ticket_creator_name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-secondary-700">{callback.assigned_to_admin_name}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(callback.status)}`}>
                                {callback.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-secondary-700">{callback.notes || '-'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {callbackTotalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-secondary-600">
                        Page {callbackPage} of {callbackTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCallbackPage(prev => Math.max(1, prev - 1))}
                          disabled={callbackPage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCallbackPage(prev => Math.min(callbackTotalPages, prev + 1))}
                          disabled={callbackPage === callbackTotalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Disputes View */}
          {viewMode === 'disputes' && (
            <>
              <div className="mb-6 flex gap-4">
                <select
                  value={disputeStatusFilter}
                  onChange={(e) => {
                    setDisputeStatusFilter(e.target.value as DisputeStatus | '');
                    setDisputePage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="resolved">Resolved</option>
                  <option value="rejected">Rejected</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  value={disputeTypeFilter}
                  onChange={(e) => {
                    setDisputeTypeFilter(e.target.value as DisputeType | '');
                    setDisputePage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Types</option>
                  <option value="transaction">Transaction</option>
                  <option value="settlement">Settlement</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <RefreshCw className="animate-spin text-primary-500" size={32} />
                </div>
              ) : disputes.length === 0 ? (
                <div className="text-center py-12 text-secondary-600">
                  <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>No disputes found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Dispute #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Subject</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Creator</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Priority</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Assigned To</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {disputes.map((dispute) => (
                          <tr key={dispute.dispute_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-medium text-secondary-900">{dispute.dispute_number}</span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-secondary-900">{dispute.subject}</div>
                              <div className="text-xs text-secondary-500 mt-1 line-clamp-1">{dispute.description}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-secondary-900">{dispute.creator_name}</div>
                              <div className="text-xs text-secondary-500">{dispute.creator_email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-secondary-700 capitalize">{dispute.dispute_type}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(dispute.priority)}`}>
                                {dispute.priority}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(dispute.status)}`}>
                                {dispute.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-secondary-700">
                                {dispute.assigned_admin_name || 'Unassigned'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                              {new Date(dispute.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleViewDispute(dispute)}
                                className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                              >
                                <Eye size={18} />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {disputeTotalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-secondary-600">
                        Page {disputePage} of {disputeTotalPages}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDisputePage(prev => Math.max(1, prev - 1))}
                          disabled={disputePage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setDisputePage(prev => Math.min(disputeTotalPages, prev + 1))}
                          disabled={disputePage === disputeTotalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportDisputeManagement;

